import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCopy,
  FaSyncAlt,
  FaTimes,
  FaYoutube,
  FaFacebookSquare,
  FaClock,
  FaThumbsUp,
  FaCommentDots,
  FaShareAlt,
  FaCheck,
  FaExclamationTriangle,
  FaUsers,
  FaRupeeSign,
  FaHandHoldingUsd,
  FaPiggyBank,
  FaTh,
  FaTable,
  FaInbox,
  FaExchangeAlt,
  FaBoxOpen,
  FaListUl,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes cpFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes cpFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes cpSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes cpZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes cpBar { from { width: 0; } }
.cp-in { animation: cpFadeUp .55s cubic-bezier(.2,.7,.2,1) both; }
.cp-fade { animation: cpFade .2s ease both; }
.cp-slide { animation: cpSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.cp-zoom { animation: cpZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.cp-bar { animation: cpBar .9s cubic-bezier(.2,.7,.2,1) both; }
.cp-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.cp-press:hover { transform: translateY(-1px); }
.cp-press:active { transform: scale(.96); }
.cp-box { transition: transform .3s cubic-bezier(.2,.8,.2,1), box-shadow .3s ease; }
.cp-box:hover { transform: translateY(-5px); box-shadow: 0 24px 40px -22px rgba(15,23,42,.5); }
.cp-dash { background-image: linear-gradient(90deg, currentColor 50%, transparent 50%); background-size: 10px 1px; background-repeat: repeat-x; height: 1px; }
@media (prefers-reduced-motion: reduce) {
  .cp-in, .cp-fade, .cp-slide, .cp-zoom, .cp-bar { animation: none; }
  .cp-press:hover, .cp-press:active, .cp-box:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500";

const TYPES = [
  { value: "Youtube", label: "YouTube", icon: FaYoutube, head: "from-red-500 to-rose-700", soft: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300", text: "text-red-600" },
  { value: "Facebook", label: "Facebook", icon: FaFacebookSquare, head: "from-blue-500 to-indigo-700", soft: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300", text: "text-blue-600" },
];

const DELIVERABLES = [
  { key: "watch_time", label: "Watch hours", short: "hrs", icon: FaClock, color: "bg-sky-500" },
  { key: "likes", label: "Likes", short: "likes", icon: FaThumbsUp, color: "bg-rose-500" },
  { key: "comments", label: "Comments", short: "comments", icon: FaCommentDots, color: "bg-amber-500" },
  { key: "share", label: "Shares", short: "shares", icon: FaShareAlt, color: "bg-emerald-500" },
];

const EMPTY = { pkg_id: "", pkg_name: "", pkg_type: "Youtube", watch_time: "", likes: "", comments: "", share: "", reffer_comission: "", pkg_price: "" };

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const typeOf = (t) => (String(t || "").toLowerCase().includes("face") ? TYPES[1] : TYPES[0]);

const validate = (f) => {
  const e = {};
  if (!String(f.pkg_name).trim()) e.pkg_name = "Package name is required";
  if (f.pkg_price === "" || num(f.pkg_price) <= 0) e.pkg_price = "Enter a price more than 0";
  if (f.reffer_comission === "") e.reffer_comission = "Required (0 if no commission)";
  else if (num(f.reffer_comission) < 0 || num(f.reffer_comission) > 100) e.reffer_comission = "Must be between 0 and 100";
  DELIVERABLES.forEach((d) => {
    if (f[d.key] === "") e[d.key] = "Required (0 if not included)";
    else if (num(f[d.key]) < 0) e[d.key] = "Cannot be negative";
  });
  if (DELIVERABLES.every((d) => num(f[d.key]) === 0)) e.watch_time = e.watch_time || "Add at least one thing the customer gets";
  return e;
};

/* ================= page ================= */

const CustomerPackages = () => {
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("boxes");
  const [typeF, setTypeF] = useState("all");

  const [editor, setEditor] = useState(null); // 'add' | 'edit'
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/customer.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([post("packageList"), post("customerList").catch(() => null)]);
      setPackages(Array.isArray(p?.data) ? p.data : []);
      setCustomers(Array.isArray(c?.customers) ? c.customers : []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load packages" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ----- derived ----- */

  const usage = useMemo(() => {
    const m = {};
    customers.forEach((c) => {
      const k = String(c.pkg_id);
      if (!m[k]) m[k] = { count: 0, revenue: 0 };
      m[k].count += 1;
      m[k].revenue += num(c.paid_amount) || num(c.pkg_price);
    });
    return m;
  }, [customers]);

  const sorted = useMemo(() => [...packages].sort((a, b) => num(a.pkg_price) - num(b.pkg_price)), [packages]);
  const shown = useMemo(() => (typeF === "all" ? sorted : sorted.filter((p) => typeOf(p.pkg_type).value === typeF)), [sorted, typeF]);

  const maxOf = useMemo(() => {
    const m = {};
    DELIVERABLES.forEach((d) => (m[d.key] = Math.max(1, ...packages.map((p) => num(p[d.key])))));
    return m;
  }, [packages]);

  const stats = useMemo(
    () => ({
      count: packages.length,
      yt: packages.filter((p) => typeOf(p.pkg_type).value === "Youtube").length,
      fb: packages.filter((p) => typeOf(p.pkg_type).value === "Facebook").length,
      sold: customers.length,
      revenue: Object.values(usage).reduce((s, u) => s + u.revenue, 0),
    }),
    [packages, customers, usage]
  );

  const errors = validate(form);
  const hasErrors = Object.keys(errors).length > 0;
  const sameExists = packages.some(
    (p) => String(p.pkg_id) !== String(form.pkg_id) && String(p.pkg_name || "").trim().toLowerCase() === String(form.pkg_name).trim().toLowerCase() && typeOf(p.pkg_type).value === typeOf(form.pkg_type).value
  );

  /* ----- editor ----- */

  const openAdd = (from, switchType) => {
    const base = from ? { ...from, pkg_id: "" } : EMPTY;
    if (from && switchType) base.pkg_type = typeOf(from.pkg_type).value === "Youtube" ? "Facebook" : "Youtube";
    if (from && !switchType) base.pkg_name = `${from.pkg_name} (copy)`;
    setForm({ ...EMPTY, ...base, pkg_type: typeOf(base.pkg_type).value });
    setTouched({});
    setEditor("add");
  };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p, pkg_type: typeOf(p.pkg_type).value });
    setTouched({});
    setEditor("edit");
  };
  const closeEditor = () => !saving && setEditor(null);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setTouched({ pkg_name: 1, pkg_price: 1, reffer_comission: 1, watch_time: 1, likes: 1, comments: 1, share: 1 });
    if (hasErrors) {
      toast.current?.show({ severity: "error", summary: "Check the form", detail: Object.values(errors)[0] });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        pkg_name: String(form.pkg_name).trim(),
        pkg_type: form.pkg_type,
        watch_time: num(form.watch_time),
        likes: num(form.likes),
        comments: num(form.comments),
        share: num(form.share),
        reffer_comission: num(form.reffer_comission),
        pkg_price: num(form.pkg_price),
      };
      if (editor === "edit") payload.pkg_id = form.pkg_id;
      const res = await post("packageAdd", payload);
      // server answers { error: 0 } for add and { status: true } for update
      if (res?.error === 0 || res?.status === true) {
        toast.current?.show({ severity: "success", summary: "Saved", detail: res.message || "Package saved" });
        setEditor(null);
        fetchAll();
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: res?.message || res?.error || "Could not save package" });
      }
    } catch (e) {
      console.error(e);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not save package" });
    }
    setSaving(false);
  };

  const runDelete = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      const res = await post("packageDelete", { pkg_id: confirm.pkg_id });
      if (res?.error === 0) {
        toast.current?.show({ severity: "success", summary: "Deleted", detail: res.message || "Package deleted" });
        setConfirm(null);
        fetchAll();
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: res?.message || "Could not delete package" });
      }
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not delete package" });
    }
    setWorking(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (editor) {
        if (e.key === "Escape") closeEditor();
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* ----- pieces ----- */

  const box = (p, i) => {
    const T = typeOf(p.pkg_type);
    const Icon = T.icon;
    const u = usage[String(p.pkg_id)] || { count: 0, revenue: 0 };
    const comm = (num(p.pkg_price) * num(p.reffer_comission)) / 100;
    const hasTwin = packages.some((x) => x.pkg_id !== p.pkg_id && String(x.pkg_name).trim().toLowerCase() === String(p.pkg_name).trim().toLowerCase() && typeOf(x.pkg_type).value !== T.value);
    return (
      <article key={p.pkg_id} className="cp-box cp-in rounded-3xl bg-white dark:bg-secondary-dark-bg border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col" style={{ animationDelay: `${i * 70}ms` }}>
        <div className={`relative bg-gradient-to-br ${T.head} text-white px-5 pt-5 pb-6`}>
          <span className="pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/15 blur-xl" />
          <div className="relative flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/90">
              <Icon /> {T.label}
            </span>
            <span className="text-[11px] text-white/70">#{p.pkg_id}</span>
          </div>
          <h3 className="relative mt-2 text-xl font-extrabold leading-tight break-words">{p.pkg_name}</h3>
          <p className="relative mt-2 text-4xl font-black tabular-nums">{money(p.pkg_price)}</p>
        </div>

        {/* what the customer gets, like a receipt */}
        <div className="px-5 pt-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Customer gets</p>
          <ul className="mt-2 space-y-2.5">
            {DELIVERABLES.map((d) => {
              const DI = d.icon;
              const v = num(p[d.key]);
              return (
                <li key={d.key}>
                  <div className="flex items-center gap-2 text-sm">
                    <DI size={11} className={v ? "text-slate-500" : "text-slate-300"} />
                    <span className={`flex-1 ${v ? "text-slate-700 dark:text-slate-200" : "text-slate-400 line-through"}`}>{d.label}</span>
                    <b className={`tabular-nums ${v ? "text-slate-900 dark:text-white" : "text-slate-300"}`}>{v}</b>
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                    <div className={`cp-bar h-full rounded-full ${d.color}`} style={{ width: `${(v / maxOf[d.key]) * 100}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="px-5 my-4 text-slate-200 dark:text-slate-700">
          <div className="cp-dash" />
        </div>

        {/* money split */}
        <div className="px-5 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-2">
            <p className="text-[10px] text-amber-700">Referrer ({num(p.reffer_comission)}%)</p>
            <p className="text-sm font-bold tabular-nums text-amber-700">{money(comm)}</p>
          </div>
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-2">
            <p className="text-[10px] text-emerald-700">YCNP keeps</p>
            <p className="text-sm font-bold tabular-nums text-emerald-700">{money(num(p.pkg_price) - comm)}</p>
          </div>
          <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-2">
            <p className="text-[10px] text-slate-500">Sold</p>
            <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{u.count}</p>
          </div>
        </div>
        <p className="px-5 mt-2 text-[11px] text-slate-500">
          Earned <b className="text-slate-800 dark:text-slate-100 tabular-nums">{money(u.revenue)}</b> from this package
        </p>

        <div className="mt-auto pt-4">
          {!hasTwin && (
            <button type="button" onClick={() => openAdd(p, true)} className="mx-5 mb-3 w-[calc(100%-2.5rem)] h-8 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-xs font-semibold text-slate-500 hover:border-sky-400 hover:text-sky-600 inline-flex items-center justify-center gap-1.5 transition-colors">
              <FaExchangeAlt size={10} /> Make the same for {T.value === "Youtube" ? "Facebook" : "YouTube"}
            </button>
          )}
          <div className="grid grid-cols-3 border-t border-slate-100 dark:border-slate-700">
            <button type="button" onClick={() => openEdit(p)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
              <FaEdit size={11} /> Edit
            </button>
            <button type="button" onClick={() => openAdd(p)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors border-x border-slate-100 dark:border-slate-700">
              <FaCopy size={11} /> Copy
            </button>
            <button type="button" onClick={() => setConfirm(p)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors">
              <FaTrash size={11} /> Delete
            </button>
          </div>
        </div>
      </article>
    );
  };

  const field = (key, label, opts = {}) => {
    const err = touched[key] && errors[key];
    const Icon = opts.icon;
    return (
      <div className={opts.full ? "sm:col-span-2" : ""}>
        <label htmlFor={`cp-${key}`} className="flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
          {Icon && <Icon size={11} className="text-slate-400" />} {label}
        </label>
        <div className="relative">
          {opts.prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{opts.prefix}</span>}
          <input
            id={`cp-${key}`}
            type={opts.type || "number"}
            inputMode={opts.type === "text" ? undefined : "decimal"}
            min={opts.type === "text" ? undefined : 0}
            step="any"
            value={form[key]}
            placeholder={opts.placeholder}
            onChange={(e) => setField(key, e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, [key]: 1 }))}
            className={`${INPUT} h-11 ${opts.prefix ? "pl-7" : "pl-3"} ${opts.suffix ? "pr-16" : "pr-3"} ${err ? "border-rose-300 focus:ring-rose-400" : ""}`}
          />
          {opts.suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{opts.suffix}</span>}
        </div>
        {err ? <p className="text-xs text-rose-600 mt-1">{err}</p> : opts.hint ? <p className="text-xs text-slate-400 mt-1">{opts.hint}</p> : null}
      </div>
    );
  };

  const FT = typeOf(form.pkg_type);
  const FIcon = FT.icon;
  const formComm = (num(form.pkg_price) * num(form.reffer_comission)) / 100;

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="cp-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Customer</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Packages</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Promotion packs customers buy: what they get, what they pay, and what the referrer earns.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => navigate("/customer/list")} className="cp-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaListUl size={12} /> Customers
          </button>
          <button type="button" onClick={fetchAll} disabled={loading} className="cp-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button type="button" onClick={() => openAdd()} className="cp-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-sm">
            <FaPlus size={11} /> Add Package
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Packages", value: `${stats.count}`, sub: `${stats.yt} YouTube • ${stats.fb} Facebook`, icon: FaBoxOpen, tint: "bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300" },
          { label: "Customers bought", value: stats.sold, sub: "across all packages", icon: FaUsers, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300" },
          { label: "Revenue", value: money(stats.revenue), sub: "from customer packages", icon: FaRupeeSign, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300" },
          { label: "Price range", value: sorted.length ? `${money(sorted[0].pkg_price)} – ${money(sorted[sorted.length - 1].pkg_price)}` : "-", sub: "cheapest to costliest", icon: FaHandHoldingUsd, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300" },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`${CARD} cp-in p-4 flex items-center gap-3`} style={{ animationDelay: `${i * 50}ms` }}>
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.tint}`}>
                <Icon />
              </span>
              <div className="min-w-0">
                <p className="text-lg font-bold text-slate-900 dark:text-white tabular-nums truncate">{loading && !packages.length ? "–" : c.value}</p>
                <p className="text-xs text-slate-500 truncate">
                  {c.label} • {c.sub}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="cp-in flex flex-wrap items-center justify-between gap-3 mb-4" style={{ animationDelay: "200ms" }}>
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
          {[{ value: "all", label: "All" }, ...TYPES].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.value} type="button" onClick={() => setTypeF(t.value)} className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${typeF === t.value ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                {Icon && <Icon className={t.text} size={12} />} {t.label}
              </button>
            );
          })}
        </div>
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
          {[
            { id: "boxes", icon: FaTh, label: "Cards" },
            { id: "compare", icon: FaTable, label: "Compare" },
          ].map((v) => {
            const Icon = v.icon;
            return (
              <button key={v.id} type="button" onClick={() => setView(v.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${view === v.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                <Icon size={11} /> {v.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {loading && !packages.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[440px] rounded-3xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
          ))}
        </div>
      ) : !shown.length ? (
        <div className={`${CARD} px-5 py-16 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={36} />
          <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{packages.length ? "No package for this platform" : "No packages yet"}</p>
          <button type="button" onClick={() => openAdd()} className="cp-press mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-sky-600 text-white text-sm font-semibold">
            <FaPlus size={11} /> Create a package
          </button>
        </div>
      ) : view === "boxes" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch">
          {shown.map(box)}
          <button type="button" onClick={() => openAdd()} className="cp-in cp-press rounded-3xl border-2 border-dashed border-slate-300 dark:border-slate-600 min-h-[360px] flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50/50 dark:hover:bg-sky-900/10">
            <span className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <FaPlus />
            </span>
            <span className="font-semibold">Add a package</span>
          </button>
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden cp-in`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 font-medium text-left">Package</th>
                  <th className="px-3 py-3 font-medium text-left">Type</th>
                  {DELIVERABLES.map((d) => (
                    <th key={d.key} className="px-3 py-3 font-medium text-left whitespace-nowrap">
                      {d.label}
                    </th>
                  ))}
                  <th className="px-3 py-3 font-medium text-right">Commission</th>
                  <th className="px-3 py-3 font-medium text-right">Price</th>
                  <th className="px-3 py-3 font-medium text-right">Sold</th>
                  <th className="px-3 py-3 pr-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {shown.map((p) => {
                  const T = typeOf(p.pkg_type);
                  const Icon = T.icon;
                  return (
                    <tr key={p.pkg_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">{p.pkg_name}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${T.soft}`}>
                          <Icon size={10} /> {T.label}
                        </span>
                      </td>
                      {DELIVERABLES.map((d) => (
                        <td key={d.key} className="px-3 py-3 min-w-[90px]">
                          <span className="tabular-nums text-slate-800 dark:text-slate-100">{num(p[d.key])}</span>
                          <div className="mt-1 h-1 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden max-w-[80px]">
                            <div className={`cp-bar h-full ${d.color}`} style={{ width: `${(num(p[d.key]) / maxOf[d.key]) * 100}%` }} />
                          </div>
                        </td>
                      ))}
                      <td className="px-3 py-3 text-right tabular-nums">
                        <p className="font-semibold text-amber-700">{money((num(p.pkg_price) * num(p.reffer_comission)) / 100)}</p>
                        <p className="text-xs text-slate-500">{num(p.reffer_comission)}%</p>
                      </td>
                      <td className="px-3 py-3 text-right font-bold tabular-nums text-emerald-700">{money(p.pkg_price)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{usage[String(p.pkg_id)]?.count || 0}</td>
                      <td className="px-3 py-3 pr-4">
                        <div className="flex justify-end gap-1.5">
                          <button type="button" onClick={() => openEdit(p)} className="cp-press w-8 h-8 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center justify-center" aria-label="Edit">
                            <FaEdit size={11} />
                          </button>
                          <button type="button" onClick={() => openAdd(p)} className="cp-press w-8 h-8 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center justify-center" aria-label="Copy">
                            <FaCopy size={11} />
                          </button>
                          <button type="button" onClick={() => setConfirm(p)} className="cp-press w-8 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center" aria-label="Delete">
                            <FaTrash size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================= Editor ================= */}
      {editor && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="cp-fade absolute inset-0 bg-slate-900/40" onClick={closeEditor} />
          <aside className="cp-slide relative w-full max-w-4xl h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs text-slate-500">{editor === "edit" ? `Package #${form.pkg_id}` : "New package"}</p>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{editor === "edit" ? "Edit Package" : "Add Package"}</h2>
              </div>
              <button type="button" onClick={closeEditor} className="cp-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-5">
                <div className="lg:col-span-3 space-y-5">
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Platform</p>
                    <div className="grid grid-cols-2 gap-2">
                      {TYPES.map((t) => {
                        const Icon = t.icon;
                        const on = form.pkg_type === t.value;
                        return (
                          <button key={t.value} type="button" onClick={() => setField("pkg_type", t.value)} className={`cp-press h-12 rounded-xl border-2 inline-flex items-center justify-center gap-2 font-semibold ${on ? `border-transparent bg-gradient-to-br ${t.head} text-white` : "border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300"}`}>
                            <Icon /> {t.label} {on && <FaCheck size={10} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {field("pkg_name", "Package Name", { type: "text", full: true, placeholder: "e.g. YCNP STARTER" })}
                    {sameExists && <p className="sm:col-span-2 -mt-3 text-xs text-amber-600">A {FT.label} package with this name already exists.</p>}
                    {field("pkg_price", "Price", { prefix: "₹", placeholder: "10", icon: FaRupeeSign })}
                    {field("reffer_comission", "Referrer Commission", { suffix: "%", placeholder: "2", hint: "Paid to the user who brings the customer", icon: FaHandHoldingUsd })}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3">What the customer gets</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {DELIVERABLES.map((d) => (
                        <React.Fragment key={d.key}>{field(d.key, d.label, { suffix: d.short, placeholder: "0", icon: d.icon, hint: "0 if not included" })}</React.Fragment>
                      ))}
                    </div>
                  </div>

                  {editor === "edit" && (usage[String(form.pkg_id)]?.count || 0) > 0 && (
                    <p className="flex items-start gap-2 rounded-xl bg-sky-50 dark:bg-sky-900/20 text-sky-900 dark:text-sky-100 text-xs p-3">
                      <FaUsers className="mt-0.5 shrink-0" /> {usage[String(form.pkg_id)].count} customers bought this package. New targets show on their progress straight away.
                    </p>
                  )}
                </div>

                {/* Preview */}
                <div className="lg:col-span-2">
                  <div className="lg:sticky lg:top-0 space-y-4">
                    <p className="text-xs font-semibold text-slate-500">Preview</p>
                    <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg">
                      <div className={`bg-gradient-to-br ${FT.head} text-white px-5 pt-5 pb-6`}>
                        <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/90">
                          <FIcon /> {FT.label}
                        </p>
                        <p className="mt-1 text-xl font-extrabold break-words">{String(form.pkg_name).trim() || "Package name"}</p>
                        <p className="mt-2 text-4xl font-black tabular-nums">{money(form.pkg_price)}</p>
                      </div>
                      <ul className="p-4 space-y-2 text-sm bg-white dark:bg-slate-800">
                        {DELIVERABLES.map((d) => {
                          const DI = d.icon;
                          const v = num(form[d.key]);
                          return (
                            <li key={d.key} className="flex items-center gap-2">
                              <DI size={11} className={v ? "text-slate-500" : "text-slate-300"} />
                              <span className={`flex-1 ${v ? "text-slate-700 dark:text-slate-200" : "text-slate-400 line-through"}`}>{d.label}</span>
                              <b className="tabular-nums text-slate-900 dark:text-white">{v}</b>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3">
                        <p className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                          <FaHandHoldingUsd size={10} /> Referrer gets
                        </p>
                        <p className="text-lg font-bold tabular-nums text-amber-700">{money(formComm)}</p>
                      </div>
                      <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3">
                        <p className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
                          <FaPiggyBank size={10} /> YCNP keeps
                        </p>
                        <p className="text-lg font-bold tabular-nums text-emerald-700">{money(num(form.pkg_price) - formComm)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <span className="hidden sm:inline text-xs text-slate-500">Ctrl + Enter to save</span>
              <div className="flex gap-2 ml-auto">
                <button type="button" onClick={closeEditor} disabled={saving} className="cp-press h-10 px-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
                  Cancel
                </button>
                <button type="button" onClick={save} disabled={saving} className="cp-press inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold disabled:opacity-60">
                  {saving ? <FaSyncAlt className="animate-spin" size={12} /> : <FaCheck size={11} />}
                  {saving ? "Saving..." : editor === "edit" ? "Save changes" : "Add package"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Delete confirm ================= */}
      {confirm && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="cp-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="cp-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center">
              <FaTrash size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              Delete "{confirm.pkg_name}" ({typeOf(confirm.pkg_type).label})?
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">New customers will not be able to buy it. This cannot be undone.</p>
            {(usage[String(confirm.pkg_id)]?.count || 0) > 0 && (
              <p className="mt-3 flex items-start gap-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200 text-sm p-3">
                <FaExclamationTriangle className="mt-0.5 shrink-0" />
                <span>
                  <b>{usage[String(confirm.pkg_id)].count} customers</b> bought this package. Their package name and delivery targets will disappear from the Customer List. Edit the package instead if you only want to change it.
                </span>
              </p>
            )}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="cp-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Keep it
              </button>
              <button type="button" onClick={runDelete} disabled={working} className="cp-press flex-1 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {working ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPackages;